/**
 * Inline "You: <connected> · +N more · edit list" bar shown at the top of a
 * contact's Activity (conversation) panel. It surfaces — and lets you edit — the
 * set of YOUR email addresses used to match a conversation to you. Mail a contact
 * sent to one of your other addresses (Apple/iCloud/secondary) that forwards into
 * your connected mailbox is then paired to you and shows in the chat (#3724-adjacent
 * forwarded-mail work). Reads/writes `provider_integrations.config.userEmailAliases`
 * via the email-proxy `/user-aliases` endpoint.
 *
 * While editing, it renders as a modal within the Activity panel: a backdrop dims
 * and disables the conversation below, the editor card floats at the top, and a
 * click on the backdrop cancels. (Requires the panel to be `position: relative`.)
 */
import { useEffect, useState } from 'react';
import { http } from '@/lib/http';
import { emailApiPath } from '@/lib/email-api';

interface ContactConvoIdentityBarProps {
  /** Called after a successful save so the caller can refetch the thread with the
   *  new alias set (newly-matched messages then appear without a manual reload). */
  onChange?: () => void;
}

type AliasResponse = { connected: string[]; aliases: string[] };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactConvoIdentityBar({ onChange }: ContactConvoIdentityBarProps) {
  const [connected, setConnected] = useState<string[]>([]);
  const [aliases, setAliases] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get<AliasResponse>(`${emailApiPath()}/user-aliases`);
        if (cancelled) return;
        setConnected(Array.isArray(data?.connected) ? data.connected : []);
        setAliases(Array.isArray(data?.aliases) ? data.aliases : []);
        setLoaded(true);
      } catch {
        /* non-fatal — the bar just stays hidden if aliases can't load */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Need a connected mailbox to have a meaningful "you".
  if (!loaded || connected.length === 0) return null;

  const openEdit = () => { setDraft([...aliases]); setEditing(true); };

  const save = async () => {
    setSaving(true);
    try {
      const clean = [...new Set(
        draft.map((a) => a.trim().toLowerCase()).filter((a) => a && EMAIL_RE.test(a)),
      )];
      const { data } = await http.put<AliasResponse>(`${emailApiPath()}/user-aliases`, { aliases: clean });
      setAliases(Array.isArray(data?.aliases) ? data.aliases : clean);
      setEditing(false);
      onChange?.();
    } catch {
      /* keep the editor open on failure so the user can retry */
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', padding: '6px 16px', borderBottom: '1px solid #F1F3F4', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>You: {connected[0]}{aliases.length > 0 ? ` · +${aliases.length} more` : ''}</span>
        <button type="button" onClick={openEdit} style={{ background: 'none', border: 'none', color: 'var(--color-brand)', cursor: 'pointer', fontSize: 12, padding: 0 }}>
          edit list
        </button>
      </div>
    );
  }

  return (
    // Modal within the Activity panel: top:48 = the panel header height (the
    // Activity/✕ row stays usable); the backdrop dims + disables the conversation.
    <div style={{ position: 'absolute', top: 48, left: 0, right: 0, bottom: 0, zIndex: 40, display: 'flex', flexDirection: 'column' }}>
      <div
        onClick={() => setEditing(false)}
        style={{ position: 'absolute', inset: 0, background: 'rgba(20,22,26,0.4)' }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', zIndex: 1, padding: '10px 16px 12px', borderBottom: '1px solid #DADCE0', background: 'var(--color-surface)', boxShadow: '0 8px 20px rgba(0,0,0,0.15)' }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>Your email addresses</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6 }}>
          Mail forwarded here from these is matched to you in conversations.
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {connected[0]} <span style={{ fontSize: 11, color: 'var(--color-text-faint)' }}>(connected)</span>
        </div>
        {draft.map((a, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <input
              type="email"
              value={a}
              onChange={(e) => setDraft(draft.map((v, j) => (j === i ? e.target.value : v)))}
              placeholder="email@example.com"
              style={{ flex: 1, padding: '6px 8px', fontSize: 13, border: '1px solid #DADCE0', borderRadius: 6, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)' }}
            />
            <button type="button" onClick={() => setDraft(draft.filter((_, j) => j !== i))} title="Remove" style={{ flexShrink: 0, width: 30, border: '1px solid var(--color-border)', borderRadius: 6, background: 'none', color: 'var(--color-text-faint)', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}>
              ×
            </button>
          </div>
        ))}
        <button type="button" onClick={() => setDraft([...draft, ''])} style={{ marginTop: 6, fontSize: 12, fontWeight: 500, color: 'var(--color-brand)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
          + Add email
        </button>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="button" disabled={saving} onClick={save} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: 'var(--color-brand)', border: 'none', borderRadius: 6, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => setEditing(false)} style={{ padding: '5px 12px', fontSize: 12, color: 'var(--color-text-muted)', background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
